import { Check, AlertCircle } from "lucide-react"
import { Progress } from "@/modules/shared/ui/components/progress"

export interface Step {
    id: number
    title: string
    icon: React.ComponentType<{ className?: string }>
}

interface EmployeeOnboardingMultiStepFormHeaderProps {
    steps: Step[]
    currentStep: number
    stepStatus: boolean[]
    stepVisited?: boolean[]
    onStepChange: (stepIndex: number) => void
    isReadOnly?: boolean
    stepDescriptions?: Record<number, string>
}

export function EmployeeOnboardingMultiStepFormHeader({
    steps,
    currentStep,
    stepStatus,
    stepVisited = [],
    onStepChange,
    stepDescriptions = {},
}: EmployeeOnboardingMultiStepFormHeaderProps) {
    const getStepState = (index: number) => {
        if (currentStep === index) return "current"
        if (stepStatus[index]) return "valid"
        if (stepVisited[index] && !stepStatus[index]) return "invalid"
        return "default"
    }
    return (
        <header className="bg-card border-b border-border px-4 py-4 sm:py-6">
            <div className="max-w-4xl mx-auto">
                <div className="mb-6 hidden lg:block">
                    <div className="flex items-center justify-between">
                        {steps.map((step, index) => {
                            const state = getStepState(index)
                            return (
                                <div key={step.id} className="flex items-center flex-1">
                                    <button
                                        onClick={() => onStepChange(index)}
                                        className="flex items-center group"
                                    >
                                        <div
                                            className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                                                state === "current"
                                                    ? "bg-blue-500 text-white"
                                                    : state === "valid"
                                                        ? "bg-green-500 text-white"
                                                        : state === "invalid"
                                                            ? "bg-red-500 text-white"
                                                            : "bg-muted text-muted-foreground group-hover:bg-muted/70"
                                            }`}
                                        >
                                            {state === "valid" ? (
                                                <Check size={16} />
                                            ) : state === "invalid" ? (
                                                <AlertCircle size={16} />
                                            ) : (
                                                <step.icon className="w-4 h-4" />
                                            )}
                                        </div>
                                        <span
                                            className={`ml-2 text-sm font-medium hidden sm:inline ${
                                                state === "current"
                                                    ? "text-blue-600"
                                                    : state === "valid"
                                                        ? "text-green-600"
                                                        : state === "invalid"
                                                            ? "text-red-600"
                                                            : "text-muted-foreground"
                                            }`}
                                        >
                                            {step.title}
                                        </span>
                                    </button>
                                    {index < steps.length - 1 && (
                                        <div className="flex-1 h-0.5 bg-border mx-2"></div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-foreground">
                        Step {currentStep + 1} of {steps.length}: {steps[currentStep].title}
                    </h2>
                    {stepDescriptions[currentStep] && (
                        <p className="text-sm text-muted-foreground mt-1">
                            {stepDescriptions[currentStep]}
                        </p>
                    )}
                    <div className="lg:hidden mt-2">
                        <Progress value={(currentStep + 1) / steps.length * 100} className="h-2" />
                    </div>
                </div>
            </div>
        </header>
    )
}
